/**
 * feAI – Frontend
 * src/hooks/usePermissions.ts
 *
 * Hook per caricare e fornire i permessi effettivi dell'utente corrente.
 * Viene usato da AppShell per mostrare/nascondere le voci di navigazione
 * in base ai permessi configurati nella sezione "Ruoli e Permessi".
 */

import { useEffect, useState, useCallback } from 'react';
import { fetchMyPermissions } from '../lib/permissionsApi';
import type { EffectivePagePermission } from '../lib/permissionsApi';

export function usePermissions() {
  const [permissions, setPermissions] = useState<EffectivePagePermission[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const perms = await fetchMyPermissions();
      setPermissions(perms);
    } catch (err) {
      // Se il modulo non è migrato / non disponibile, fallback: nessun permesso
      console.warn('Impossibile caricare i permessi, fallback a default:', err);
      setError(err instanceof Error ? err.message : 'Errore permessi');
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  /** Recupera il permesso effettivo per una pagina specifica */
  const getPagePermission = useCallback(
    (pageId: string): EffectivePagePermission | undefined => {
      if (!permissions) return undefined;
      return permissions.find(x => x.page_id === pageId);
    },
    [permissions]
  );

  /** Verifica se una pagina è visibile all'utente (can_view) */
  const canViewPage = useCallback(
    (pageId: string): boolean => {
      if (!permissions) {
        if (pageId.startsWith('admin/')) return false;
        return true;
      }
      const p = permissions.find(x => x.page_id === pageId);
      return p ? p.can_view : false;
    },
    [permissions]
  );

  /** Verifica se una pagina è utilizzabile dall'utente (can_use) */
  const canUsePage = useCallback(
    (pageId: string): boolean => {
      if (!permissions) {
        if (pageId.startsWith('admin/')) return false;
        return true;
      }
      const p = permissions.find(x => x.page_id === pageId);
      return p ? p.can_use : false;
    },
    [permissions]
  );

  /** Verifica se l'utente può vedere tutti i record (can_view_all) */
  const canViewAll = useCallback(
    (pageId: string): boolean => {
      if (!permissions) return false;
      const p = permissions.find(x => x.page_id === pageId);
      return p ? p.can_view_all : false;
    },
    [permissions]
  );

  /** Verifica se l'utente può caricare file (can_upload) */
  const canUpload = useCallback(
    (pageId: string): boolean => {
      if (!permissions) return false;
      const p = permissions.find(x => x.page_id === pageId);
      return p ? p.can_upload : false;
    },
    [permissions]
  );

  return { permissions, loading, error, reload, canViewPage, canUsePage, canViewAll, canUpload, getPagePermission };
}